import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from 'react-toastify';
import DropdownTable from '../../components/DropdownTable';
import { formatValueWithRub } from "../../utils/priceTextFormat";
import { orderItemsForSingleTask } from "./logistRequestGrouping";
import "../../theme/redesign/modal.css";

const LogistRequestModal = ({
  show,
  onHide,
  logistFormData,
  logistFormErrors,
  handleItemChange,
  validateLogistForm,
  regionOptions,
  isLoadingLogistOptions,
  setLogistFormData,
  dealId,
  clientName,
  addressAndCoordinates,
  flows,
  findFlowIdByMaterial,
  deliveryDate,
  paymentType = 'Нал',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  if (!show) return null;

  // Получаем короткое название региона
  const getShortRegionName = (regionName) => {
    if (!regionName) return 'СПБ';
    const name = regionName.toUpperCase();
    if (name.includes('СПБ') || name.includes('САНКТ') || name.includes('ПЕТЕРБУРГ')) return 'СПБ';
    if (name.includes('МСК') || name.includes('МОСКВ')) return 'МСК';
    if (name.includes('КРД') || name.includes('КРАСНОДАР')) return 'КРД';
    return name;
  };

  // Получаем Bitrix ID региона
  const getRegionBitrixId = (shortName) => {
    const regionMap = {
      'СПБ': '1',
      'МСК': '2',
      'КРД': '3',
    };
    return regionMap[shortName] || '1';
  };

  // Форматируем дату поставки в человеческий вид
  const formatDeliveryDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Битрикс может возвращать дату в формате ISO или как timestamp
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      // Формат: "17 января 2026 г."
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }) + ' г.';
    } catch (e) {
      return dateString;
    }
  };

  const isDeliveryItem = (item) => {
    const categoryStr = (item.category || '').toLowerCase();
    const nameStr = (item.nomenclature || item.material || '').toLowerCase();
    return categoryStr.includes('доставка') || nameStr.includes('доставка');
  };

  const isRaznoeItem = (item) => {
    const categoryStr = (item.category || '').toLowerCase().trim();
    return categoryStr === 'разное разное';
  };

  const handleSubmit = async () => {
    if (!validateLogistForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const address = addressAndCoordinates?.address || '';
      const regionName = logistFormData.regionName || '';
      const regionBitrixId = getRegionBitrixId(getShortRegionName(regionName));

      // Разделяем позиции доставки и товарные позиции
      const deliveryItems = logistFormData.items.filter(item => isDeliveryItem(item));
      const productItems = logistFormData.items.filter(item => !isDeliveryItem(item));

      // Если выбрана только доставка — задачи не создаём
      if (productItems.length === 0 && deliveryItems.length > 0) {
        toast.info('Выбрана только доставка — задача логисту не создаётся. Добавьте товарные позиции.');
        setIsSubmitting(false);
        return;
      }

      // Отделяем "Разное Разное": если есть другие товары — прикрепляем к каждой задаче
      const raznoeItems = productItems.filter(item => isRaznoeItem(item));
      const mainProductItems = productItems.filter(item => !isRaznoeItem(item));
      // Если есть не-разное товары → raznoe идут как приложение; иначе — отправляем все как обычно
      const iterItems = mainProductItems.length > 0 ? mainProductItems : productItems;
      const attachedRaznoeItems = mainProductItems.length > 0 ? raznoeItems : [];

      // Формируем блок доставки для вставки в комментарий (BBCode)
      const deliveryBlock = deliveryItems.length > 0
        ? `\n\n[b]Доставка и услуги:[/b]\n` + deliveryItems.map(d => {
            const qty = d.quantity ? `${d.quantity} ${d.unit || d.measure || 'шт'}` : '';
            const price = d.competitorPrice ? ` - ${formatValueWithRub(d.competitorPrice)}` : '';
            const comment = d.comment ? ` (${d.comment})` : '';
            return `- ${d.material}${qty ? ' - ' + qty : ''}${price}${comment}`;
          }).join('\n')
        : '';

      // Формируем блок "Разное Разное" (BBCode) для вставки в каждую задачу
      const raznoeBlock = attachedRaznoeItems.length > 0
        ? `\n\n[b]Также в поставке (Разное):[/b]\n` + attachedRaznoeItems.map(d => {
            const qty = d.quantity ? `${d.quantity} ${d.unit || d.measure || 'шт'}` : '';
            const price = d.competitorPrice ? ` - ${formatValueWithRub(d.competitorPrice)}` : '';
            const comment = d.comment ? ` (${d.comment})` : '';
            return `- ${d.material}${qty ? ' - ' + qty : ''}${price}${comment}`;
          }).join('\n')
        : '';

      // Одна задача на сделку со всеми товарами — как у бота и экрана товаров. Отдел, проект,
      // тег и поток — у победившей по количеству группы; сервер сверяет routingItems с правилами
      // распределения и по весу может отдать задачу проекту правила.
      const { winner, items: orderedItems } = orderItemsForSingleTask(iterItems);
      const flowId = findFlowIdByMaterial ? findFlowIdByMaterial(winner, flows || []) : 25;
      const tag = winner.tagId || 'Общестрой';
      const itemsCount = orderedItems.length + attachedRaznoeItems.length;

      const productLines = orderedItems.map((item) => {
        const quantity = parseFloat(item.quantity) || 0;
        const unit = item.unit || item.measure || 'шт';
        const competitorPrice = formatValueWithRub(item.competitorPrice);
        return `- ${item.material} - ${quantity} ${unit}${competitorPrice ? ' (цена конкурентов: ' + competitorPrice + ')' : ''}`;
      }).join('\n');

      const fullComment = `[b]Клиент:[/b] ${clientName || 'Не указан'}
[b]Населенный пункт:[/b] ${address}
${deliveryDate ? '[b]Дата поставки материала:[/b] ' + formatDeliveryDate(deliveryDate) : ''}
[b]Форма оплаты:[/b] ${paymentType}

[b]Продукция:[/b]
${productLines}
${logistFormData.comment ? '[b]Комментарий:[/b] ' + logistFormData.comment : ''}${raznoeBlock}${deliveryBlock}`;

      const requestData = {
        dealId: dealId,
        clientName: clientName || 'Не указан',
        tag,
        price: winner.competitorPrice || '',
        comment: fullComment,
        departmentId: winner.departmentId || '',
        groupId: winner.groupId || '',
        material: `${tag}: ${itemsCount} товаров (сделка ${dealId})`,
        // Товары задачи — сервер сверяет их с правилами распределения и выбирает проект по весу
        routingItems: orderedItems.map((item) => ({ nomenclature: item.material, quantity: parseFloat(item.quantity) || 0 })),
        materialFromDB: winner.materialFromDB || '',
        materialId: winner.materialId || '',
        materialBitrixId: winner.materialBitrixId || '',
        regionId: regionBitrixId,
        regionName: getShortRegionName(regionName), // Регион для определения логиста на бэкенде
        flowId: flowId,
        fromCalculator: true,
        // В калькуляторе одна кнопка — «Отправить запрос», это запрос логисту.
        requestKind: 'logist',
        requestSource: 'calculator', // KAL-81: сервер переводит стадию сделки по источнику
      };

      console.log('📤 Отправляем запрос логисту (одна задача на сделку):', requestData);

      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/logistics/create-task`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Ошибка создания задачи');
      }

      console.log('✅ Задача создана:', result);

      // Стадию сделки после запроса переводит сервер (KAL-81).

      toast.success('Запрос логисту успешно отправлен!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      onHide();
    } catch (error) {
      console.error('❌ Ошибка отправки запроса:', error);
      setSubmitError(error.message || 'Ошибка при отправке запроса');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="rd-modal-overlay" role="presentation">
      <div
        className="rd-modal rd-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rd-logist-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="rd-modal__close"
          onClick={onHide}
          aria-label="Закрыть"
          title="Закрыть"
        />
        <div className="rd-modal__head">
          <h3 id="rd-logist-title" className="rd-modal__title">Запрос логисту</h3>
        </div>

        <div className="rd-modal__body">
          {submitError && (
            <div className="rd-modal__note rd-modal__note--error" style={{ marginBottom: '15px' }}>
              {submitError}
            </div>
          )}

          {isLoadingLogistOptions ? (
            <div className="rd-modal__note rd-modal__note--loading">
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span>Загрузка данных…</span>
            </div>
          ) : (
            <Form>
              <Form.Group className="form-group mb-4">
                <Form.Label>Регион</Form.Label>
                <DropdownTable
                  id="region"
                  options={regionOptions}
                  selectedValue={logistFormData.regionName}
                  onChange={(value) => {
                    const selectedOption = regionOptions.find(opt => opt.value === value);
                    setLogistFormData(prev => ({
                      ...prev,
                      regionId: value,
                      regionName: selectedOption?.label || value
                    }));
                  }}
                />
                {logistFormErrors.region && <div className="error-text">{logistFormErrors.region}</div>}
              </Form.Group>

              {logistFormData.items.map((item, index) => (
                <div key={item.id} className="rd-logist-item">
                  <div className="rd-logist-item__head">
                    <span className="rd-logist-item__num">Товар {index + 1}</span>
                    {item.tagId && (
                      <span className="rd-logist-item__tag">Тег: <strong>{item.tagId}</strong></span>
                    )}
                  </div>

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="form-group mb-2">
                        <Form.Label>Материал</Form.Label>
                        <div className="rd-logist-item__material">{item.material}</div>
                        {logistFormErrors[`${item.id}_material`] && <div className="error-text">{logistFormErrors[`${item.id}_material`]}</div>}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="form-group mb-2">
                        <Form.Label>Количество</Form.Label>
                        <Form.Control
                          type="text"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                          placeholder="Кол-во"
                        />
                        <div className="rd-logist-item__hint">Ед. изм.: <strong>{item.unit || item.measure || 'шт'}</strong></div>
                        {logistFormErrors[`${item.id}_quantity`] && <div className="error-text">{logistFormErrors[`${item.id}_quantity`]}</div>}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="form-group mb-2">
                        <Form.Label>Цена конкурента</Form.Label>
                        <Form.Control
                          type="text"
                          value={item.competitorPrice}
                          onChange={(e) => handleItemChange(item.id, 'competitorPrice', e.target.value)}
                          placeholder="Цена конк."
                        />
                        {logistFormErrors[`${item.id}_competitorPrice`] && <div className="error-text">{logistFormErrors[`${item.id}_competitorPrice`]}</div>}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}

              <Form.Group className="form-group mb-2">
                <Form.Label>Комментарий</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={logistFormData.comment || ''}
                  onChange={(e) => setLogistFormData(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Общий комментарий по всем товарам"
                />
              </Form.Group>
            </Form>
          )}
        </div>

        <div className="rd-modal__foot">
          <div className="rd-modal__actions">
            <button
              type="button"
              className="rd-modal__btn rd-modal__btn--ghost"
              onClick={onHide}
              disabled={isSubmitting}
            >
              Отмена
            </button>
            <button
              type="button"
              className="rd-modal__btn rd-modal__btn--primary"
              onClick={handleSubmit}
              disabled={isLoadingLogistOptions || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  {' '}Отправка…
                </>
              ) : (
                'Отправить'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Используем Portal для выноса модалки на верхний уровень DOM
  return ReactDOM.createPortal(modalContent, document.body);
};

export default LogistRequestModal;
